import { v4 as uuidv4 } from 'uuid';
import { ContentSchema } from '../../definitions/schemas';
import { DOCUMENT_FORMATS, IMAGE_CONTENT_MIMES } from '../../config/variables';
import { getExtension, getFilename } from '../../lib/File';
import {
  ProgressCallback,
  SaveAudioParams,
  SaveDocumentParams,
  SaveEmbeddableParams,
  SaveImageParams,
  SaveTextParams,
  SaveUrlParams,
  SaveVideoParams,
  UploadContext,
} from './CreateParams';
import { SyncPreferenceValues } from '../../enums/StorageProvider';
import CloudStorage, {
  UploadAudioResult,
  UploadDocumentResult,
  UploadImageResult,
  UploadVideoResult,
} from '../CloudStorage';
import ContentInterface, {
  CreateContentInput,
} from '../../graphql/Content/ContentInterface';
import ContentType from '../../enums/ContentType';

const CONTENT_NAME_LENGTH = 90;

export interface CreateContentContext {
  libraryVisible?: boolean;
  /**
   * Naming priority
   * - Save context name
   * - File name
   * - Null (not ideal)
   */
  name?: string;
  personal?: boolean;
  questionId?: string;
  requestedReferenceId?: string;
  sectionHeaderId?: string;
  /**
   * Whether the sectionHeaderId must be added as part of the payload to
   * create a content. Necessary for the scenario where a section header
   * is assigned to the content but it's one that's exclusive (custom) for a room
   */
  shouldSaveSectionHeaderId?: boolean;
  storageProviderFileId?: string;
  storageSyncPreference?: SyncPreferenceValues;
}

export interface CreateContentVersionContext {
  id: string;
  storageProviderFileId?: string;
  storageSyncPreference?: SyncPreferenceValues;
}

export type SaveContentContext =
  | CreateContentContext
  | CreateContentVersionContext;

export const getDocumentType = (
  mime: string,
  fallbackType = 'unknown'
): string => {
  const format = DOCUMENT_FORMATS.find(({ mimes }) => mimes.includes(mime));
  return format ? format.name.toLowerCase() : fallbackType;
};

export const getDocumentLabel = (
  mime: string,
  fallbackType = 'unknown'
): string => {
  const format = DOCUMENT_FORMATS.find(({ mimes }) => mimes.includes(mime));
  return format ? format.label : fallbackType;
};

// @see https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
export const hasURL = (text: string): boolean => {
  const URL_REGEX =
    // eslint-disable-next-line no-useless-escape
    /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi;
  return URL_REGEX.test(text);
};

/**
 * Destructuring properties from the create context that must not be sent
 * as part of the payload to create a content. This is a convenience method
 * to support custom configurations for the create context
 */
const getCreateContext = (createContext: CreateContentContext) => {
  const { shouldSaveSectionHeaderId, ...realCreateContext } = createContext;
  return realCreateContext;
};

const getContentName = (name: string): string =>
  name.trim().substr(0, CONTENT_NAME_LENGTH);

function saveContent(
  saveContext: Partial<CreateContentInput>,
  type: ContentType,
  contactId?: string
) {
  const obj = {
    ...saveContext,
    contactId,
    type,
  } as CreateContentInput;

  return new ContentInterface(contactId).create(obj);
}

function uploadAudioAndSave(
  context: UploadContext,
  file: File,
  onSave: (result: UploadAudioResult) => Promise<ContentSchema>,
  progressCallback: ProgressCallback
) {
  return new Promise<ContentSchema>((resolve, reject) => {
    CloudStorage.uploadAudio(context, file, progressCallback).then(
      (result) => onSave(result).then(resolve, reject),
      (error: string) => reject(error)
    );
  });
}

function uploadDocumentAndSave(
  context: UploadContext,
  file: File,
  onSave: (result: UploadDocumentResult) => Promise<ContentSchema>,
  progressCallback: ProgressCallback
) {
  return new Promise<ContentSchema>((resolve, reject) => {
    // @see https://docs.aws.amazon.com/AmazonS3/latest/dev-retired/UsingMetadata.html
    const safeFilename = `document-${Date.now()}-${uuidv4()}`;
    CloudStorage.uploadDocument(
      context,
      file,
      // @warning using file.name creates a lot of errors when signing cloudfront urls
      `${safeFilename}/${safeFilename}.${getExtension(file)}`,
      progressCallback,
      true
    ).then(
      (result) => onSave(result).then(resolve, reject),
      (error: string) => reject(error)
    );
  });
}

function uploadVideoAndSave(
  context: UploadContext,
  file: File,
  onSave: (result: UploadVideoResult) => Promise<ContentSchema>,
  progressCallback: ProgressCallback
) {
  return new Promise<ContentSchema>((resolve, reject) => {
    CloudStorage.uploadVideo(context, file, progressCallback).then(
      (result) => onSave(result).then(resolve, reject),
      (error: string) => reject(error)
    );
  });
}

function uploadImageAndSave(
  context: UploadContext,
  file: File,
  onSave: (result: UploadImageResult) => Promise<ContentSchema>,
  progressCallback: ProgressCallback
) {
  const filename = `image-${Date.now()}-${uuidv4()}`;

  return new Promise<ContentSchema>((resolve, reject) => {
    CloudStorage.uploadImage(
      context,
      file,
      `${filename}/${filename}.${getExtension(file)}`,
      progressCallback,
      IMAGE_CONTENT_MIMES
    ).then(
      (result) => onSave(result).then(resolve, reject),
      (error: string) => reject(error)
    );
  });
}

class ContentService {
  public static createAudio(
    uploadContext: UploadContext,
    createContext: CreateContentContext,
    params: SaveAudioParams
  ): Promise<ContentSchema> {
    const {
      afterSave = (content: ContentSchema) => Promise.resolve(content),
      file,
      progressCallback,
    } = params;
    const { contactId } = uploadContext;
    const newCreateContext = getCreateContext(createContext);

    if (file && progressCallback) {
      return uploadAudioAndSave(
        uploadContext,
        params.file,
        ({ uri: url, totalSeconds }) =>
          saveContent(
            { ...newCreateContext, totalSeconds, url },
            ContentType.AUDIO,
            contactId
          ).then(afterSave),
        progressCallback
      );
    }
    return saveContent(newCreateContext, ContentType.AUDIO, contactId);
  }

  public static createNewAudioVersion(
    uploadContext: UploadContext,
    createVersionContext: CreateContentVersionContext,
    params: SaveAudioParams
  ): Promise<ContentSchema> {
    const { file, progressCallback } = params;
    if (file && progressCallback) {
      return uploadAudioAndSave(
        uploadContext,
        file,
        ({ uri, totalSeconds }) =>
          new ContentInterface()
            .createVersion({
              ...createVersionContext,
              url: uri,
              totalSeconds,
            })
            .then(({ content }) => content as ContentSchema),
        progressCallback
      );
    }
    return new ContentInterface()
      .createVersion({
        ...createVersionContext,
      })
      .then(({ content }) => content as ContentSchema);
  }

  public static createDocument(
    uploadContext: UploadContext,
    createContext: CreateContentContext,
    params: SaveDocumentParams
  ): Promise<ContentSchema> {
    const { file, progressCallback } = params;
    const { contactId } = uploadContext;
    const newCreateContext = getCreateContext(createContext);

    if (file && progressCallback) {
      return uploadDocumentAndSave(
        uploadContext,
        file,
        ({ filename, mime, uri: url }) => {
          const name = createContext.name || getContentName(getFilename(file));
          return saveContent(
            { ...newCreateContext, filename, mime, name, url },
            ContentType.DOCUMENT,
            contactId
          );
        },
        progressCallback
      );
    }
    return saveContent(newCreateContext, ContentType.DOCUMENT, contactId);
  }

  public static createNewDocumentVersion(
    uploadContext: UploadContext,
    createVersionContext: CreateContentVersionContext,
    params: SaveDocumentParams
  ): Promise<ContentSchema> {
    const { file, progressCallback } = params;
    if (file && progressCallback) {
      return uploadDocumentAndSave(
        uploadContext,
        file!,
        ({ uri: url, filename, mime }) =>
          new ContentInterface()
            .createVersion({
              ...createVersionContext,
              filename,
              mime,
              url,
            })
            .then(({ content }) => content as ContentSchema),
        progressCallback!
      );
    }
    return new ContentInterface()
      .createVersion({
        ...createVersionContext,
      })
      .then(({ content }) => content as ContentSchema);
  }

  public static createEmbeddable(
    uploadContext: UploadContext,
    createContext: CreateContentContext,
    params: SaveEmbeddableParams
  ) {
    const {
      afterSave = (content: ContentSchema) => Promise.resolve(content),
      ...embeddableParams
    } = params;
    const { contactId } = uploadContext;

    const obj = {
      ...getCreateContext(createContext),
      ...embeddableParams,
      contactId,
      type: ContentType.EMBEDDABLE,
    } as CreateContentInput;

    return new ContentInterface(contactId).create(obj).then(afterSave);
  }

  public static createNewEmbeddableVersion(
    createVersionContext: CreateContentVersionContext,
    params: SaveEmbeddableParams
  ) {
    const {
      afterSave = (content: ContentSchema) => Promise.resolve(content),
      ...embeddableParams
    } = params;
    return new ContentInterface()
      .createVersion({
        ...createVersionContext,
        ...embeddableParams,
      })
      .then(({ content }) => content as ContentSchema)
      .then(afterSave);
  }

  public static createImage(
    uploadContext: UploadContext,
    createContext: CreateContentContext,
    params: SaveImageParams
  ): Promise<ContentSchema> {
    const { file, mime, progressCallback } = params;
    const { contactId } = uploadContext;
    const newCreateContext = getCreateContext(createContext);

    if (file && progressCallback) {
      return uploadImageAndSave(
        uploadContext,
        file,
        ({ uri: url }) => {
          const name = createContext.name || getContentName(getFilename(file));
          return saveContent(
            { ...newCreateContext, mime, name, url },
            ContentType.IMAGE,
            contactId
          );
        },
        progressCallback
      );
    }
    return saveContent(newCreateContext, ContentType.IMAGE, contactId);
  }

  public static createNewImageVersion(
    uploadContext: UploadContext,
    createVersionContext: CreateContentVersionContext,
    params: SaveImageParams
  ): Promise<ContentSchema> {
    const { file, mime, progressCallback } = params;

    if (file && progressCallback) {
      return uploadImageAndSave(
        uploadContext,
        file!,
        ({ uri: url }) =>
          new ContentInterface()
            .createVersion({ ...createVersionContext, mime, url })
            .then(({ content }) => content as ContentSchema),
        progressCallback!
      );
    }

    return new ContentInterface()
      .createVersion({ ...createVersionContext, mime })
      .then(({ content }) => content as ContentSchema);
  }

  public static createText(
    uploadContext: UploadContext,
    createContext: CreateContentContext,
    params: SaveTextParams
  ) {
    const { html, text } = params;
    const { contactId } = uploadContext;
    const obj = {
      ...getCreateContext(createContext),
      contactId,
      html,
      text,
      type: ContentType.TEXT,
    } as CreateContentInput;
    return new ContentInterface(contactId).create(obj);
  }

  public static createNewTextVersion(
    createVersionContext: CreateContentVersionContext,
    params: SaveTextParams
  ) {
    const { text, html } = params;
    return new ContentInterface()
      .createVersion({
        ...createVersionContext,
        html,
        text,
      })
      .then(({ content }) => content as ContentSchema);
  }

  public static createUrl(
    uploadContext: UploadContext,
    createContext: CreateContentContext,
    params: SaveUrlParams
  ) {
    const {
      afterSave = (content: ContentSchema) => Promise.resolve(content),
      ...urlParams
    } = params;
    const { contactId } = uploadContext;
    const obj = {
      ...getCreateContext(createContext),
      ...urlParams,
      contactId,
      type: ContentType.URL,
    } as CreateContentInput;
    return new ContentInterface(contactId).create(obj).then(afterSave);
  }

  public static createNewUrlVersion(
    createVersionContext: CreateContentVersionContext,
    params: SaveUrlParams
  ) {
    const {
      afterSave = (content: ContentSchema) => Promise.resolve(content),
      ...urlParams
    } = params;
    return new ContentInterface()
      .createVersion({
        ...createVersionContext,
        ...urlParams,
      })
      .then(({ content }) => content as ContentSchema)
      .then(afterSave);
  }

  public static createVideo(
    uploadContext: UploadContext,
    createContext: CreateContentContext,
    params: SaveVideoParams
  ): Promise<ContentSchema> {
    const {
      afterSave = (content: ContentSchema) => Promise.resolve(content),
      file,
      progressCallback,
      tags = null,
    } = params;
    const { contactId } = uploadContext;
    const newCreateContext = getCreateContext(createContext);

    if (file && progressCallback) {
      return uploadVideoAndSave(
        uploadContext,
        file,
        ({ uri: url, totalSeconds }) =>
          saveContent(
            { ...newCreateContext, tags: tags || [], totalSeconds, url },
            ContentType.VIDEO,
            contactId
          ).then(afterSave),
        progressCallback
      );
    }
    return saveContent(newCreateContext, ContentType.VIDEO, contactId).then(
      afterSave
    );
  }

  public static createNewVideoVersion(
    uploadContext: UploadContext,
    createVersionContext: CreateContentVersionContext,
    params: SaveVideoParams
  ): Promise<ContentSchema> {
    const {
      afterSave = (content: ContentSchema) => Promise.resolve(content),
      file,
      progressCallback,
    } = params;
    if (file && progressCallback) {
      return uploadVideoAndSave(
        uploadContext,
        file,
        ({ uri: url, totalSeconds }) =>
          new ContentInterface()
            .createVersion({
              ...createVersionContext,
              totalSeconds,
              url,
            })
            .then(({ content }) => content)
            .then(afterSave),
        progressCallback
      );
    }
    return new ContentInterface()
      .createVersion({
        ...createVersionContext,
      })
      .then(({ content }) => content)
      .then(afterSave);
  }
}

export default ContentService;
